import React, { useEffect, useState } from "react";
import { Leaf, Recycle, FileWarning, Workflow, Gauge, Droplets } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Badge2 } from "@/components/ComplianceBadge";

const COLORS = ["#0B2545", "#134074", "#10B981", "#F59E0B", "#EF4444", "#64748B"];

function Stat({ icon: Icon, label, value, sub, tone = "green" }) {
  const tones = { green: "bg-emerald-500", navy: "bg-[#0B2545]", amber: "bg-amber-500", red: "bg-rose-500" };
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg text-white ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <p className="mt-4 text-3xl font-extrabold text-[#0B2545]">{value}</p>
      <p className="mt-1 text-sm font-medium text-slate-600">{label}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export default function EnvironmentPanel() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/environment/stats").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="py-20 text-center text-slate-400">Carregando painel ambiental...</div>;

  return (
    <div data-testid="environment-page">
      <PageHeader icon={Leaf} title="Painel Ambiental (ISO 14001)" subtitle="Resíduos, licenças a vencer e progresso do Sistema de Gestão Ambiental" />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Recycle} label="Resíduos destinados (total)" value={`${d.total_kg} kg`} sub={`${d.mtr_total} MTRs`} tone="green" />
        <Stat icon={Gauge} label="Progresso ISO 14001" value={`${d.iso_progress}%`} sub={`${d.iso_done}/${d.iso_total} itens`} tone="navy" />
        <Stat icon={Workflow} label="Aspectos significativos" value={d.aspects_significant} sub={`${d.aspects_total} aspectos mapeados`} tone="amber" />
        <Stat icon={FileWarning} label="Licenças/itens a vencer" value={d.alerts_count} sub={`${d.alerts_expired} vencidos`} tone={d.alerts_expired > 0 ? "red" : "green"} />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center gap-2"><Droplets className="h-4 w-4 text-emerald-600" /><h3 className="text-base font-semibold text-[#134074]">Resíduos destinados por mês (kg)</h3></div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.waste_months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} />
              <Tooltip />
              <Bar dataKey="kg" name="Resíduos (kg)" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-[#134074]">Resíduos por classe</h3>
          {d.waste_by_type.length === 0 ? <p className="py-20 text-center text-sm text-slate-400">Sem dados</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={d.waste_by_type} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} label>
                  {d.waste_by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-[#134074]">Licenças e itens ambientais a vencer (30 dias)</h3>
        {d.alerts.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Nenhuma pendência ambiental próxima do vencimento 🎉</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {d.alerts.map((a, i) => (
              <div key={i} className="flex cursor-pointer items-center justify-between py-3 hover:bg-slate-50" onClick={() => navigate(a.module === "iso14001" ? "/iso14001" : "/documentos")} data-testid={`env-alert-${i}`}>
                <div><p className="text-sm font-medium text-slate-700">{a.title}</p><p className="text-xs text-slate-400">{a.label} · {a.extra}</p></div>
                <Badge2 color={a.level === "vencido" ? "red" : "yellow"}>{a.level === "vencido" ? `Vencido há ${-a.days}d` : `Vence em ${a.days}d`}</Badge2>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
